import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useContext, useState, useRef, useEffect } from 'react';
import { AppContext } from '@/App';
import { 
  Home, Users, LogOut, ChevronDown, Settings, Menu, X,
  Factory, Package, Truck, Warehouse, FolderKanban, Wrench, UserCog,
  FileText, Calendar, Recycle, FlaskConical, ClipboardList, Layers,
  BoxesIcon, Boxes, Cog, Camera, Scale, ClipboardCheck, AlertCircle, FileInput, ShoppingCart, FileSearch, Ship,
  CheckCircle, Clock, AlertOctagon, FileImage, DollarSign, CalendarDays, Network, AlertTriangle, BarChart3, Globe, Folder, Building2, Shield,
  Sun, Moon
} from 'lucide-react';

function Layout() {
  const { user, logout, theme, toggleTheme } = useContext(AppContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileExpandedMenu, setMobileExpandedMenu] = useState(null);
  const dropdownRef = useRef(null);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setMobileExpandedMenu(null);
  }, [location.pathname]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Check if user has access to a specific page
  // Priority: 1. Custom page_access (if exists) 2. Role-based permissions
  const hasPageAccess = (path) => {
    if (!user) return false;
    
    // Admin and Manager always have full access
    if (user.role === 'Admin' || user.role === 'Manager') return true;
    
    // Check custom page_access first (granular permissions)
    if (user.page_access && typeof user.page_access === 'object') {
      const access = user.page_access[path];
      if (access === 'view' || access === 'edit' || access === 'full') return true;
      // If explicitly set to none or not in page_access, deny
      if (Object.keys(user.page_access).length > 0) {
        // User has custom permissions, so check if this path is included
        // Also check for partial path matches (e.g., /production matches /production/data-entry)
        const hasMatch = Object.keys(user.page_access).some(p => 
          path === p || path.startsWith(p + '/') || p.startsWith(path + '/')
        );
        if (!hasMatch) return false;
      }
    }
    
    // Fall back to role-based permissions
    if (!user?.permissions) return true;
    const allowedPages = user.permissions.pages || [];
    if (allowedPages.includes('*')) return true;
    return allowedPages.some(p => path.startsWith(p));
  };

  // Check if user can edit (not just view) a specific page
  const canEditPage = (path) => {
    if (!user) return false;
    
    // Admin and Manager always have edit access
    if (user.role === 'Admin' || user.role === 'Manager') return true;
    
    // Check custom page_access for edit or full permission
    if (user.page_access && typeof user.page_access === 'object') {
      const access = user.page_access[path];
      return access === 'edit' || access === 'full';
    }
    
    // Fall back to role-based actions
    return user.permissions?.actions?.includes('update') || false;
  };

  // Check if user can delete data on a specific page
  const canDeletePage = (path) => {
    if (!user) return false;
    
    // Admin and Manager always have delete access
    if (user.role === 'Admin' || user.role === 'Manager') return true;
    
    // Check custom page_access for full permission only
    if (user.page_access && typeof user.page_access === 'object') {
      return user.page_access[path] === 'full';
    }
    
    // Fall back to role-based actions
    return user.permissions?.actions?.includes('delete') || false;
  };

  // Check if user has access to a menu category
  const hasMenuAccess = (menuLabel) => {
    if (!user) return false;
    
    // Admin and Manager always have full access
    if (user.role === 'Admin' || user.role === 'Manager') return true;
    
    // If user has custom page_access, check if any page in the category is accessible
    if (user.page_access && typeof user.page_access === 'object' && Object.keys(user.page_access).length > 0) {
      // Find the category's pages from navItems
      const category = navItems.find(item => item.label === menuLabel);
      if (category?.items) {
        return category.items.some(subItem => {
          const access = user.page_access[subItem.path];
          return access === 'view' || access === 'edit' || access === 'full';
        });
      }
    }
    
    // Fall back to role-based menu access
    if (!user?.permissions) return true;
    const allowedMenus = user.permissions.menuItems || [];
    if (allowedMenus.includes('*')) return true;
    return allowedMenus.includes(menuLabel);
  };

  // Filter menu items based on user permissions
  const filterMenuItems = (items) => {
    if (!user) return [];
    
    // Admin and Manager see everything
    if (user.role === 'Admin' || user.role === 'Manager') {
      return items;
    }
    
    // If user has custom page_access, use that
    if (user.page_access && typeof user.page_access === 'object' && Object.keys(user.page_access).length > 0) {
      return items.filter(item => {
        const access = user.page_access[item.path];
        return access === 'view' || access === 'edit' || access === 'full';
      });
    }
    
    // Fall back to role-based permissions
    if (!user?.permissions || user.permissions.pages?.includes('*')) {
      return items;
    }
    return items.filter(item => hasPageAccess(item.path));
  };

  // Department-specific navigation with dropdowns
  const navItems = [
    { path: '/', label: 'Dashboard', icon: Home },
    { 
      label: 'Production', 
      icon: Factory,
      dropdown: true,
      items: [
        { path: '/production/data-entry', label: 'Data Entry (Excel)', icon: FileText },
        { path: '/production/furnace-dashboard', label: 'Furnace Dashboard', icon: Factory },
        { path: '/production/report', label: 'Production Report', icon: FileText },
        { path: '/production/duty-roster', label: 'Duty Roster', icon: Calendar },
        { path: '/production/material-recovery', label: 'Material Recovery', icon: Recycle },
        { path: '/production/crucible-report', label: 'Crucible Report', icon: FlaskConical },
        { path: '/production/production-plan', label: 'Production Plan', icon: ClipboardList },
        { path: '/production/batch-plan', label: 'Batch Plan', icon: Layers },
      ]
    },
    { 
      label: 'Raw Material', 
      icon: Package,
      dropdown: true,
      items: [
        { path: '/raw-material/stock', label: 'Aluminium Scrap', icon: BoxesIcon },
        { path: '/raw-material/ferrous-scrap', label: 'Ferrous Scrap', icon: BoxesIcon },
        { path: '/raw-material/reds-heavies', label: 'Reds & Heavies', icon: BoxesIcon },
        { path: '/raw-material/dross-stock', label: 'Dross', icon: Package },
        { path: '/raw-material/processing-machines', label: 'Processing Machines', icon: Cog },
        { path: '/raw-material/processing-plan', label: 'Daily Processing Plan', icon: ClipboardList },
        { path: '/raw-material/duty-roster', label: 'Duty Roster', icon: Calendar },
      ]
    },
    { 
      label: 'Dispatch', 
      icon: Truck,
      dropdown: true,
      items: [
        { path: '/dispatch/records', label: 'Dispatch Records', icon: FileText },
        { path: '/dispatch/finished-goods', label: 'Finished Goods Stock', icon: BoxesIcon },
        { path: '/dispatch/customer-orders', label: 'Customer Orders', icon: FileText },
        { path: '/dispatch/orders', label: 'Order Details', icon: ClipboardList },
        { path: '/dispatch/delivery', label: 'PO vs Delivery', icon: Truck },
        { path: '/dispatch/weighment', label: 'Weighment Details', icon: Scale },
        { path: '/dispatch/duty-roster', label: 'Duty Roster', icon: Calendar },
      ]
    },
    { 
      label: 'Stores', 
      icon: Warehouse,
      dropdown: true,
      items: [
        { path: '/stores/consumable-stock', label: 'Consumable Stock', icon: BoxesIcon },
        { path: '/stores/service-consumption', label: 'Service Consumption', icon: BoxesIcon },
        { path: '/stores/grn', label: 'GRN', icon: FileText },
        { path: '/stores/qa', label: 'QA', icon: ClipboardCheck },
        { path: '/stores/rejections', label: 'Rejections', icon: AlertCircle },
        { path: '/stores/material-requisitions', label: 'Material Requisitions', icon: FileInput },
        { path: '/stores/purchase-order', label: 'Purchase Order', icon: ShoppingCart },
        { path: '/stores/payments', label: 'Payments', icon: DollarSign },
        { path: '/stores/po-vs-delivered', label: 'PO vs Delivered', icon: BarChart3 },
        { path: '/stores/tenders', label: 'Tenders & Enquiries', icon: FileSearch },
      ]
    },
    { 
      label: 'Projects', 
      icon: FolderKanban,
      dropdown: true,
      items: [
        { path: '/projects/ongoing', label: 'Ongoing Projects', icon: FolderKanban },
        { path: '/projects/completed', label: 'Projects Completed', icon: CheckCircle },
        { path: '/projects/upcoming', label: 'Upcoming Projects', icon: Clock },
        { path: '/projects/purchase-orders', label: 'Project Purchase Orders', icon: ShoppingCart },
        { path: '/projects/grn', label: 'Project GRN', icon: Package },
        { path: '/projects/qa', label: 'Project QA', icon: ClipboardCheck },
        { path: '/projects/material-consumption', label: 'Project Material Consumption', icon: Boxes },
        { path: '/projects/service-consumption', label: 'Project Service Consumption', icon: Wrench },
        { path: '/projects/payments', label: 'Project Payments', icon: DollarSign },
      ]
    },
    { 
      label: 'Maintenance', 
      icon: Wrench,
      dropdown: true,
      items: [
        { path: '/maintenance/machinery', label: 'Plant & Machinery', icon: Cog },
        { path: '/maintenance/log', label: 'Maintenance Log', icon: FileText },
        { path: '/maintenance/shutdown', label: 'Report a Shutdown', icon: AlertOctagon },
        { path: '/maintenance/duty-roster', label: 'Duty Roster', icon: Calendar },
        { path: '/maintenance/drawings', label: 'Drawings', icon: FileImage },
      ]
    },
    { 
      label: 'HR', 
      icon: UserCog,
      dropdown: true,
      items: [
        { path: '/hr/master-roll', label: 'Employment Directory', icon: Users },
        { path: '/hr/salary', label: 'Master Roll', icon: DollarSign },
        { path: '/hr/leave', label: 'Leave Management', icon: CalendarDays },
        { path: '/hr/calendar', label: 'Holiday Calendar', icon: Calendar },
        { path: '/hr/attendance', label: 'Attendance & Requests', icon: ClipboardCheck },
        { path: '/hr/payroll', label: 'Attendance Payroll', icon: DollarSign },
        { path: '/hr/org-structure', label: 'Organisation Structure', icon: Network },
        { path: '/hr/grievances', label: 'Grievances', icon: AlertTriangle },
      ]
    },
    { 
      label: 'Imports', 
      icon: Ship,
      dropdown: true,
      items: [
        { path: '/imports/supplier-dashboard', label: 'Supplier Dashboard', icon: Globe },
        { path: '/imports/purchase-order', label: 'Purchase Order', icon: ShoppingCart },
        { path: '/imports/grn', label: 'GRN (Goods Receipt)', icon: FileText },
        { path: '/imports/claims', label: 'Claims', icon: AlertTriangle },
        { path: '/imports/claims-dashboard', label: 'Claims Dashboard', icon: BarChart3 },
        { path: '/imports/sales-contract', label: 'Sales Contract Directory', icon: FileText },
        { path: '/imports/documents', label: 'Documents', icon: Folder },
        { path: '/imports/po-vs-delivered', label: 'PO vs Delivered', icon: BarChart3 },
        { path: '/imports/shipments', label: 'Shipment Tracking', icon: Ship },
      ]
    },
    { 
      label: 'Master Data', 
      icon: Layers,
      dropdown: true,
      items: [
        { path: '/master-data/production', label: 'Production', icon: Factory },
        { path: '/master-data/raw-material', label: 'Raw Material', icon: Package },
        { path: '/master-data/dispatch', label: 'Dispatch', icon: Truck },
        { path: '/master-data/stores', label: 'Stores', icon: Warehouse },
        { path: '/master-data/projects', label: 'Projects', icon: FolderKanban },
        { path: '/master-data/maintenance', label: 'Maintenance', icon: Wrench },
        { path: '/master-data/hr', label: 'HR', icon: UserCog },
        { path: '/master-data/imports', label: 'Imports', icon: Ship },
      ]
    },
  ];

  const isActiveDropdown = (items) => {
    return items?.some(item => location.pathname.startsWith(item.path.split('?')[0]));
  };

  const toggleDropdown = (label) => {
    setActiveDropdown(activeDropdown === label ? null : label);
  };

  const isDark = theme === 'dark';

  return (
    <div className={`min-h-screen bg-gradient-to-br ${isDark ? 'from-slate-950 via-slate-900 to-slate-950 text-slate-100' : 'from-slate-50 via-gray-50 to-slate-100 text-slate-900'}`}>
      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <div className={`fixed inset-y-0 left-0 w-72 ${isDark ? 'bg-slate-900 text-slate-100 shadow-slate-950/80' : 'bg-white text-slate-900 shadow-2xl'} shadow-2xl z-50 transform transition-transform duration-300 ease-in-out lg:hidden ${
        mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        {/* Mobile Sidebar Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-[#1a237e] to-[#283593]">
          <div className="flex items-center space-x-3">
            <img src="/logo.jpg" alt="Swift Strips" className="h-10 w-10 rounded-lg" />
            <div>
              <p className="text-white font-bold text-sm">Swift Strips India</p>
              <p className="text-white/70 text-xs">ERP System</p>
            </div>
          </div>
          <button 
            onClick={() => setMobileMenuOpen(false)}
            className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg"
          >
            <X size={20} />
          </button>
        </div>

        {/* Mobile User Info */}
        <div className="p-4 border-b border-gray-100 bg-gray-50">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 bg-gradient-to-br from-[#e65100] to-[#f57c00] rounded-full flex items-center justify-center">
              <span className="text-white font-bold">{user?.name?.charAt(0).toUpperCase() || 'U'}</span>
            </div>
            <div>
              <p className="font-medium text-gray-900 text-sm">{user?.name}</p>
              <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                user?.role === 'Admin' ? 'bg-purple-100 text-purple-700' :
                user?.role === 'DataEntry' ? 'bg-blue-100 text-blue-700' :
                user?.role === 'Manager' ? 'bg-green-100 text-green-700' :
                'bg-gray-100 text-gray-700'
              }`}>
                {user?.role === 'DataEntry' ? 'Data Entry' : user?.role}
              </span>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        <nav className="flex-1 overflow-y-auto p-2 max-h-[calc(100vh-220px)]">
          {navItems.map((item) => {
            const Icon = item.icon;
            
            if (item.dropdown) {
              if (!hasMenuAccess(item.label)) return null;
              const filteredItems = filterMenuItems(item.items);
              if (filteredItems.length === 0) return null;
              
              const isExpanded = mobileExpandedMenu === item.label;
              const isActive = isActiveDropdown(filteredItems);
              
              return (
                <div key={item.label} className="mb-1">
                  <button
                    onClick={() => setMobileExpandedMenu(isExpanded ? null : item.label)}
                    className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition-all ${
                      isActive ? 'bg-[#1a237e]/10 text-[#1a237e]' : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <Icon size={18} />
                      <span className="font-medium text-sm">{item.label}</span>
                    </div>
                    <ChevronDown size={16} className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {isExpanded && (
                    <div className="ml-4 mt-1 space-y-0.5 border-l-2 border-gray-200 pl-3">
                      {filteredItems.map((subItem) => {
                        const SubIcon = subItem.icon;
                        return (
                          <NavLink
                            key={subItem.path}
                            to={subItem.path}
                            className={({ isActive }) =>
                              `flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-all ${
                                isActive
                                  ? 'bg-[#e65100]/10 text-[#e65100] font-medium'
                                  : 'text-gray-600 hover:bg-gray-100'
                              }`
                            }
                          >
                            <SubIcon size={16} />
                            <span>{subItem.label}</span>
                          </NavLink>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            if (item.path === '/' && user?.permissions?.hideDashboard) return null;
            
            return (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === '/'}
                className={({ isActive }) =>
                  `flex items-center space-x-3 px-3 py-2.5 rounded-lg mb-1 transition-all ${
                    isActive
                      ? 'bg-[#1a237e] text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`
                }
              >
                <Icon size={18} />
                <span className="font-medium text-sm">{item.label}</span>
              </NavLink>
            );
          })}
        </nav>

        {/* Mobile Sidebar Footer */}
        <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-gray-200 bg-white">
          <div className="flex items-center space-x-2">
            <NavLink
              to="/camera"
              className="flex-1 flex items-center justify-center space-x-2 px-3 py-2 bg-gray-100 rounded-lg text-gray-700 hover:bg-gray-200"
            >
              <Camera size={16} />
              <span className="text-sm">Camera</span>
            </NavLink>
            <NavLink
              to="/settings"
              className="flex-1 flex items-center justify-center space-x-2 px-3 py-2 bg-gray-100 rounded-lg text-gray-700 hover:bg-gray-200"
            >
              <Settings size={16} />
              <span className="text-sm">Settings</span>
            </NavLink>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center space-x-2 px-3 py-2.5 mt-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition"
          >
            <LogOut size={16} />
            <span className="text-sm font-medium">Logout</span>
          </button>
        </div>
      </div>

      {/* Header */}
      <header className={`backdrop-blur-md border-b sticky top-0 z-30 shadow-sm ${isDark ? 'bg-slate-900/90 border-slate-700/80' : 'bg-white/95 border-gray-200/80'}`}>
        <div className="px-3 sm:px-4 py-2">
          <div className="flex items-center justify-between">
            {/* Mobile Menu Button + Logo */}
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setMobileMenuOpen(true)}
                className="lg:hidden p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                data-testid="mobile-menu-btn"
              >
                <Menu size={22} />
              </button>
              <NavLink to="/dashboard" className="flex-shrink-0">
                <img 
                  src="/logo.jpg" 
                  alt="Swift Strips India" 
                  className="h-8 w-8 sm:h-10 sm:w-10 object-contain rounded-lg"
                />
              </NavLink>
            </div>

            {/* Desktop Navigation - Hidden on mobile */}
            <nav className="hidden lg:flex items-center space-x-1" ref={dropdownRef}>
              {navItems.map((item) => {
                const Icon = item.icon;
                
                // Dropdown menu item
                if (item.dropdown) {
                  // Check if user has access to this menu category
                  if (!hasMenuAccess(item.label)) {
                    return null;
                  }
                  
                  // Filter sub-items based on permissions
                  const filteredItems = filterMenuItems(item.items);
                  if (filteredItems.length === 0) {
                    return null; // Hide menu if no accessible items
                  }
                  
                  const isActive = isActiveDropdown(filteredItems);
                  return (
                    <div key={item.label} className="relative">
                      <button
                        onClick={() => toggleDropdown(item.label)}
                        className={`flex items-center space-x-1.5 px-3 py-2 rounded-lg transition-all duration-200 text-sm ${
                          isActive || activeDropdown === item.label
                            ? 'bg-gradient-to-r from-[#1a237e] to-[#283593] text-white shadow-md'
                            : 'text-gray-600 hover:bg-gray-100 hover:text-[#1a237e]'
                        }`}
                        data-testid={`nav-dropdown-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <Icon size={16} />
                        <span className="font-medium">{item.label}</span>
                        <ChevronDown 
                          size={14} 
                          className={`transition-transform duration-200 ${activeDropdown === item.label ? 'rotate-180' : ''}`} 
                        />
                      </button>

                      {/* Dropdown Content */}
                      {activeDropdown === item.label && (
                        <div className="absolute top-full left-0 mt-1 w-52 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-[100] animate-in fade-in slide-in-from-top-2 duration-200">
                          {filteredItems.map((subItem) => {
                            const SubIcon = subItem.icon;
                            return (
                              <NavLink
                                key={subItem.path}
                                to={subItem.path}
                                onClick={() => setActiveDropdown(null)}
                                className={({ isActive }) =>
                                  `flex items-center space-x-3 px-4 py-2.5 text-sm transition-all ${
                                    isActive
                                      ? 'bg-gradient-to-r from-orange-50 to-orange-100 text-[#e65100] border-l-3 border-[#e65100]'
                                      : 'text-gray-700 hover:bg-gray-50 hover:text-[#1a237e]'
                                  }`
                                }
                                data-testid={`nav-${subItem.label.toLowerCase().replace(/\s+/g, '-')}`}
                              >
                                <SubIcon size={16} />
                                <span>{subItem.label}</span>
                              </NavLink>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                }

                // Regular nav item (Dashboard) - hide for DataEntry users
                if (item.path === '/' && user?.permissions?.hideDashboard) {
                  return null;
                }
                
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    end={item.path === '/'}
                    onClick={() => setActiveDropdown(null)}
                    className={({ isActive }) =>
                      `flex items-center space-x-1.5 px-3 py-2 rounded-lg transition-all duration-200 text-sm ${
                        isActive
                          ? 'bg-gradient-to-r from-[#1a237e] to-[#283593] text-white shadow-md'
                          : 'text-gray-600 hover:bg-gray-100 hover:text-[#1a237e]'
                      }`
                    }
                    data-testid={`nav-${item.label.toLowerCase()}`}
                  >
                    <Icon size={16} />
                    <span className="font-medium">{item.label}</span>
                  </NavLink>
                );
              })}
            </nav>

            {/* User Menu */}
            <div className="relative flex items-center space-x-1 sm:space-x-2">
              {/* Camera Link - Hidden on mobile (available in sidebar) */}
              <NavLink
                to="/camera"
                className={({ isActive }) =>
                  `hidden sm:block p-2 rounded-lg transition-all duration-200 ${
                    isActive
                      ? 'bg-gradient-to-r from-[#1a237e] to-[#283593] text-white shadow-md'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-[#1a237e]'
                  }`
                }
                data-testid="nav-camera"
                title="Document Camera"
              >
                <Camera size={18} />
              </NavLink>

              {/* Settings Link - Hidden on mobile */}
              <NavLink
                to="/settings"
                className={({ isActive }) =>
                  `hidden sm:block p-2 rounded-lg transition-all duration-200 ${
                    isActive
                      ? 'bg-gradient-to-r from-[#1a237e] to-[#283593] text-white shadow-md'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-[#1a237e]'
                  }`
                }
                data-testid="nav-settings"
                title="Settings"
              >
                <Settings size={18} />
              </NavLink>

              {/* User Dropdown */}
              <button
                type="button"
                onClick={toggleTheme}
                className={`p-2 rounded-lg transition-all duration-200 ${isDark ? 'text-amber-300 hover:bg-slate-800' : 'text-slate-600 hover:bg-gray-100'}`}
                title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {isDark ? <Sun size={18} /> : <Moon size={18} />}
              </button>

              <button
                onClick={() => setShowUserDropdown(!showUserDropdown)}
                className={`flex items-center space-x-2 px-2 py-1.5 rounded-lg transition-all duration-200 ${isDark ? 'hover:bg-slate-800' : 'hover:bg-gray-100'}`}
                data-testid="user-menu-button"
              >
                <div className="h-8 w-8 bg-gradient-to-br from-[#e65100] to-[#f57c00] rounded-full flex items-center justify-center shadow-sm">
                  <span className="text-white font-bold text-sm">
                    {user?.name?.charAt(0).toUpperCase() || 'U'}
                  </span>
                </div>
                <div className="text-left hidden lg:block">
                  <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                  <p className="text-xs text-gray-500">{user?.role}</p>
                </div>
                <ChevronDown size={14} className="text-gray-400 hidden lg:block" />
              </button>

              {showUserDropdown && (
                <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-50 animate-in fade-in slide-in-from-top-2 duration-200">
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="text-sm font-semibold text-gray-900">{user?.name}</p>
                    <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                    <span className={`inline-block mt-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${
                      user?.role === 'Admin' ? 'bg-purple-100 text-purple-700' :
                      user?.role === 'DataEntry' ? 'bg-blue-100 text-blue-700' :
                      user?.role === 'Manager' ? 'bg-green-100 text-green-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {user?.role === 'DataEntry' ? 'Data Entry' : user?.role}
                    </span>
                  </div>
                  {user?.role === 'Admin' && (
                    <>
                      <NavLink
                        to="/settings/company"
                        onClick={() => setShowUserDropdown(false)}
                        className="flex items-center space-x-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition"
                      >
                        <Building2 size={16} />
                        <span>Company Profile</span>
                      </NavLink>
                      <NavLink
                        to="/settings/users"
                        onClick={() => setShowUserDropdown(false)}
                        className="flex items-center space-x-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition"
                      >
                        <Users size={16} />
                        <span>User Management</span>
                      </NavLink>
                      <NavLink
                        to="/settings/permissions-audit"
                        onClick={() => setShowUserDropdown(false)}
                        className="flex items-center space-x-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition"
                        data-testid="nav-permissions-audit"
                      >
                        <Shield size={16} />
                        <span>Permissions Audit</span>
                      </NavLink>
                    </>
                  )}
                  <NavLink
                    to="/settings"
                    onClick={() => setShowUserDropdown(false)}
                    className="flex items-center space-x-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition"
                  >
                    <Settings size={16} />
                    <span>Settings</span>
                  </NavLink>
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center space-x-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition"
                    data-testid="logout-button"
                  >
                    <LogOut size={16} />
                    <span>Logout</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="p-3 sm:p-4 lg:p-6">
        <Outlet />
      </main>

      {/* Footer - Hidden on mobile for more space */}
      <footer className="hidden sm:block bg-white/80 border-t border-gray-200/80 py-3 sm:py-4 mt-auto">
        <div className="px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-2">
          <div className="flex items-center space-x-2">
            <img src="/logo.jpg" alt="Swift Strips India" className="h-5 w-5 sm:h-6 sm:w-6 object-contain" />
            <span className="text-xs sm:text-sm text-gray-500">© 2025 Swift Strips India</span>
          </div>
          <div className="text-xs text-gray-400">ERP System v1.0</div>
        </div>
      </footer>
    </div>
  );
}

export default Layout;
